import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkle, ArrowClockwise, PaperPlaneTilt, Clock, CaretDown, CaretUp } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const BOARD_LABELS = {
  sales: 'AI Sales Insights',
  expenses: 'AI Expense Insights',
  purchases: 'AI Purchase Insights',
  master: 'AI Business Insights',
};

const BoardAIInsights = ({ board = 'sales' }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/analytics/ai-insights/history`, {
        params: { board },
        withCredentials: true,
      });
      setHistory(data.history || []);
      if (data.history?.length > 0 && !insights) {
        setInsights(data.history[0]);
      }
    } catch (err) { console.error('Failed to fetch AI history:', err.message); }
  }, [board, insights]);

  useEffect(() => {
    if (expanded && history.length === 0) fetchHistory();
  }, [expanded, history.length, fetchHistory]);


  const generateInsights = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/analytics/ai-insights`,
        { question: question || undefined, board },
        { withCredentials: true }
      );
      setInsights(data);
      setQuestion('');
      fetchHistory();
      toast.success('AI insights generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate insights');
    } finally {
      setLoading(false);
    }
  };

  const formatInsights = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h3 key={i} className="text-base font-bold mt-5 mb-2 text-foreground">{line.replace(/^##\s*/, '').replace(/\*\*/g, '')}</h3>;
      if (line.startsWith('### ')) return <h4 key={i} className="text-sm font-bold mt-4 mb-1.5 text-foreground">{line.replace(/^###\s*/, '').replace(/\*\*/g, '')}</h4>;
      if (line.startsWith('**') && line.endsWith('**')) return <h3 key={i} className="text-base font-bold mt-5 mb-2 text-foreground">{line.replace(/\*\*/g, '')}</h3>;
      if (line.match(/^\d+\.\s\*\*/)) return <h3 key={i} className="text-base font-bold mt-5 mb-2 text-foreground">{line.replace(/\*\*/g, '')}</h3>;
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = line.slice(2);
        const parts = content.split(/\*\*(.*?)\*\*/g);
        return (
          <div key={i} className="flex items-start gap-2 ml-2 mb-1.5">
            <span className="text-muted-foreground mt-1.5 text-[6px]">&#9679;</span>
            <span className="text-sm text-muted-foreground leading-relaxed">
              {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-foreground">{part}</strong> : part)}
            </span>
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-1">
          {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-foreground">{part}</strong> : part)}
        </p>
      );
    });
  };

  return (
    <div className="space-y-4" data-testid={`ai-insights-${board}`}>
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors text-left"
          data-testid={`ai-insights-toggle-${board}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center">
              <Sparkle size={20} weight="fill" className="text-primary" />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">{BOARD_LABELS[board] || 'AI Insights'}</div>
              <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">POWERED BY GPT-5.2</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!expanded && insights?.insights && (
              <span className="text-xs text-muted-foreground hidden sm:block max-w-[200px] truncate">
                {insights.insights.substring(0, 60)}...
              </span>
            )}
            {expanded ? <CaretUp size={18} weight="bold" className="text-muted-foreground" /> : <CaretDown size={18} weight="bold" className="text-muted-foreground" />}
          </div>
        </button>

        {expanded && (
          <div className="px-6 pb-6 pt-2 border-t border-border">
            <div className="flex items-center justify-end mb-4">
              {history.length > 0 && (
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowHistory(!showHistory)} data-testid={`ai-history-toggle-${board}`}>
                  <Clock size={14} weight="regular" className="mr-1.5" />
                  History ({history.length})
                </Button>
              )}
            </div>

            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Ask about your data..."
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="rounded-sm"
                onKeyDown={(e) => e.key === 'Enter' && !loading && generateInsights()}
                data-testid={`ai-question-input-${board}`}
              />
              <Button onClick={generateInsights} disabled={loading} className="rounded-sm whitespace-nowrap" data-testid={`generate-insights-btn-${board}`}>
                {loading ? (
                  <><ArrowClockwise size={16} weight="bold" className="mr-2 animate-spin" />Analyzing...</>
                ) : (
                  <><PaperPlaneTilt size={16} weight="bold" className="mr-2" />{insights ? 'Refresh' : 'Generate'}</>
                )}
              </Button>
            </div>

            {loading && (
              <div className="py-12 text-center" data-testid={`ai-loading-${board}`}>
                <div className="inline-flex items-center gap-3 text-muted-foreground">
                  <ArrowClockwise size={20} className="animate-spin" />
                  <span className="text-sm">Analyzing your business data with AI...</span>
                </div>
              </div>
            )}

            {!loading && insights?.insights && (
              <div className="border border-border rounded-sm p-5 bg-background" data-testid={`ai-insights-content-${board}`}>
                {insights.question && (
                  <div className="text-xs font-bold tracking-[0.15em] uppercase text-primary mb-3">Q: {insights.question}</div>
                )}
                <div className="prose-sm">{formatInsights(insights.insights)}</div>
                {insights.generated_at && (
                  <div className="mt-4 pt-3 border-t border-border text-xs text-muted-foreground">
                    Generated {new Date(insights.generated_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}

            {!loading && !insights && (
              <div className="py-12 text-center border border-dashed border-border rounded-sm" data-testid={`ai-empty-${board}`}>
                <Sparkle size={32} weight="regular" className="mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground mb-1">Get AI-powered analysis of your {board} data</p>
                <p className="text-xs text-muted-foreground">Click "Generate" or ask a specific question above</p>
              </div>
            )}
          </div>
        )}
      </div>

      {expanded && showHistory && history.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-4" data-testid={`ai-history-panel-${board}`}>
          <div className="text-sm font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3">Previous Insights</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {history.map((item, idx) => (
              <button
                key={idx}
                onClick={() => { setInsights(item); setShowHistory(false); }}
                className="w-full text-left p-3 border border-border rounded-sm hover:bg-muted/50 transition-colors"
                data-testid={`ai-history-item-${board}-${idx}`}
              >
                <div className="text-xs text-muted-foreground">
                  {new Date(item.created_at).toLocaleString()}
                  {item.question && <span className="ml-2 text-primary">Q: {item.question}</span>}
                </div>
                <div className="text-sm mt-1 line-clamp-2 text-foreground">{item.insights?.substring(0, 120)}...</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardAIInsights;
